// Rules of "Gestionar" of Gestiones IPC (design Prompts 4, 6 and 7; 04-diseno.md §3, §4.3 and §6), mirrored from
// IpcGestionService so the asesor sees every error before the POST. The server validates everything again. The lists
// are the codes the server accepts (IpcGestionConstants), not business data: the gestión comes from GET gestiones/{id}.
import { EMPTY, PRIORIDAD_LABELS, codeOf, formatDate, formatTime, labelFor } from "../../crmpostventa-shared/crmFormat";
import { datoOf } from "../../crmpostventa-rollo/gestionar/gestionarModel";
import {
  CANAL_LABELS,
  ESTADO_LABELS,
  ESTADOS_ABIERTOS,
  MOTIVO_CIERRE_LABELS,
  TIPO_LABELS,
  isOpenGestion,
  isProactiva,
  motivoLabel,
  toOptions,
} from "../ipcFormat";
import { OBSERVACION_MAX, parseInputDateTime, toServerDateTime } from "../nuevaGestion/nuevaGestionIpcModel";

export { createCorrelationId } from "../../crmpostventa-rollo/nuevaGestion/nuevaGestionModel";
export { freshEntryIds } from "../../crmpostventa-rollo/gestionar/gestionarModel";

export const ACCIONES = {
  REGISTRAR_CONTACTO: "REGISTRAR_CONTACTO",
  PEDIR_DATOS: "PEDIR_DATOS",
  DERIVAR: "DERIVAR",
  RETOMAR: "RETOMAR",
  REPROGRAMAR: "REPROGRAMAR",
  OBSERVAR: "OBSERVAR",
  CAMBIAR_PRIORIDAD: "CAMBIAR_PRIORIDAD",
  FINALIZAR: "FINALIZAR",
};

export const RESULTADO_CONTACTO_LABELS = {
  CONTESTO: "Contestó",
  NO_ATIENDE: "No atiende",
  OCUPADO: "Ocupado",
  NUMERO_INCORRECTO: "Número incorrecto",
  OTRO: "Otro",
};

export const CANAL_OPTIONS = toOptions(CANAL_LABELS, Object.keys(CANAL_LABELS));
export const RESULTADO_CONTACTO_OPTIONS = toOptions(RESULTADO_CONTACTO_LABELS, Object.keys(RESULTADO_CONTACTO_LABELS));
export const PRIORIDAD_OPTIONS = toOptions(PRIORIDAD_LABELS, Object.keys(PRIORIDAD_LABELS));
export const RESULTADO_CIERRE_OPTIONS = toOptions(ESTADO_LABELS, ["RESUELTA", "DESCARTADA"]);
// Closing motives by result (P9, provisional).
export const MOTIVOS_CIERRE = {
  RESUELTA: toOptions(MOTIVO_CIERRE_LABELS, ["RESUELTO", "CLIENTE_CONTACTADO", "RESUELTO_POR_OTRA_AREA"]),
  DESCARTADA: toOptions(MOTIVO_CIERRE_LABELS, ["SIN_RESPUESTA", "NO_CORRESPONDE", "OTRO"]),
};

// Actions by state (Prompt 4 table); Finalizar is a separate link for every open state.
const ACCIONES_ASESOR = [
  ACCIONES.REGISTRAR_CONTACTO,
  ACCIONES.PEDIR_DATOS,
  ACCIONES.DERIVAR,
  ACCIONES.REPROGRAMAR,
  ACCIONES.OBSERVAR,
  ACCIONES.CAMBIAR_PRIORIDAD,
];
const ACCIONES_ESPERA = [ACCIONES.RETOMAR, ACCIONES.REPROGRAMAR, ACCIONES.OBSERVAR, ACCIONES.CAMBIAR_PRIORIDAD];

export const actionsFor = (estado) => {
  switch (codeOf(estado)) {
    case "PENDIENTE":
    case "EN_GESTION":
      return ACCIONES_ASESOR;
    case "ESPERANDO_COMERCIO":
    case "DERIVADA":
      return ACCIONES_ESPERA;
    default:
      return [];
  }
};

export const isOpenEstado = (estado) => ESTADOS_ABIERTOS.includes(codeOf(estado));

// Responsible actions shown for a gestión that can be managed (D25), from the detail permissions: Tomar without
// responsible, Liberar on your own, Asignar… for anyone who can manage. null when it cannot be managed.
export const ownershipFor = (data, gestion, canManage) =>
  canManage
    ? {
        tomar: data?.puedeTomar ?? (gestion?.responsableUsuarioId === null || gestion?.responsableUsuarioId === undefined),
        liberar: data?.puedeLiberar ?? false,
        asignar: data?.puedeAsignar !== false,
      }
    : null;

// RadioCard title and description, primary button and success toast of each action (prototype ACTIONS). Retomar is
// named after where the gestión comes back from.
const COPY = {
  [ACCIONES.REGISTRAR_CONTACTO]: { title: "Registrar contacto", desc: "Llamaste o escribiste al comercio", btn: "Registrar contacto", toast: "Contacto registrado" },
  [ACCIONES.PEDIR_DATOS]: { title: "Pedir datos al comercio", desc: "Pasa a Esperando al comercio", btn: "Pedir datos", toast: "Datos pedidos al comercio" },
  [ACCIONES.DERIVAR]: { title: "Derivar", desc: "Pasa a Derivada, a otra área", btn: "Derivar gestión", toast: "Gestión derivada" },
  [ACCIONES.RETOMAR]: { title: "Retomar", desc: "Vuelve a En gestión", btn: "Retomar gestión", toast: "Gestión retomada" },
  [ACCIONES.REPROGRAMAR]: { title: "Reprogramar próximo contacto", desc: "Sin cambiar el estado", btn: "Reprogramar", toast: "Próximo contacto reprogramado" },
  [ACCIONES.OBSERVAR]: { title: "Agregar observación", desc: "Una nota en el historial, sin cambiar el estado", btn: "Agregar observación", toast: "Observación agregada" },
  [ACCIONES.CAMBIAR_PRIORIDAD]: { title: "Cambiar prioridad", desc: "Queda fijada a mano", btn: "Cambiar prioridad", toast: "Prioridad cambiada" },
  [ACCIONES.FINALIZAR]: { title: "Finalizar gestión", desc: "", btn: "Finalizar gestión", toast: "Gestión finalizada" },
};

const RETOMAR_TITLES = { ESPERANDO_COMERCIO: "El comercio respondió", DERIVADA: "Volvió del área" };

export const accionCopy = (accion, estado) => {
  const copy = COPY[accion] || { title: accion, desc: "", btn: "Guardar", toast: "Gestión actualizada" };
  if (accion !== ACCIONES.RETOMAR) return copy;
  return { ...copy, title: RETOMAR_TITLES[codeOf(estado)] || copy.title };
};

// Form keys in the order they appear on screen, with the id suffix of their control: the first invalid one gets the
// focus. Radio groups point at their first radio.
export const FIELD_IDS = {
  canal: "canal",
  resultado: "resultado",
  prioridad: "prioridad",
  observacion: "observacion",
  proximoContacto: "proximo-contacto",
};
export const fieldId = (key) => `crm-ipc-gestionar-${FIELD_IDS[key]}`;
export const errorId = (key) => `${fieldId(key)}-error`;
export const firstErrorKey = (errors, ids = FIELD_IDS) => Object.keys(ids).find((key) => errors[key]) || null;

export const FINALIZAR_FIELD_IDS = { resultado: "resultado", motivoCierre: "motivo-cierre", observacion: "observacion" };
export const finalizarFieldId = (key) => `crm-ipc-finalizar-${FINALIZAR_FIELD_IDS[key]}`;
export const finalizarErrorId = (key) => `${finalizarFieldId(key)}-error`;

export const EMPTY_FORM = { canal: "", resultado: "", observacion: "", proximoContacto: "", prioridad: "" };
export const EMPTY_FINALIZAR = { resultado: "", motivoCierre: "", observacion: "" };

// A new action starts from an empty form; Cambiar prioridad starts with the current priority.
export const formForAccion = (accion, gestion) =>
  accion === ACCIONES.CAMBIAR_PRIORIDAD ? { ...EMPTY_FORM, prioridad: codeOf(gestion?.prioridad) } : { ...EMPTY_FORM };

const text = (value) => String(value ?? "").trim();
const textOrNull = (value) => text(value) || null;

const OBSERVACION_OBLIGATORIA = {
  [ACCIONES.PEDIR_DATOS]: "Contá qué le falta enviar al comercio",
  [ACCIONES.DERIVAR]: "Contá a quién o a qué área la derivaste",
  [ACCIONES.OBSERVAR]: "Escribí la observación",
};
const PROXIMO_OBLIGATORIO = [ACCIONES.PEDIR_DATOS, ACCIONES.DERIVAR, ACCIONES.REPROGRAMAR];

export const proximoContactoObligatorio = (accion) => PROXIMO_OBLIGATORIO.includes(accion);
export const observacionObligatoria = (accion) => Boolean(OBSERVACION_OBLIGATORIA[accion]);

const checkObservacion = (errors, observacion, requiredMessage) => {
  if (requiredMessage && !text(observacion)) errors.observacion = requiredMessage;
  else if (text(observacion).length > OBSERVACION_MAX) {
    errors.observacion = `Acortá la observación a ${OBSERVACION_MAX} caracteres como máximo`;
  }
};

// Validation on submit, with each message as an instruction (Prompt 6). The next contact must be later than now (RN6).
export const validateAccion = (accion, form, { gestion, now = new Date() } = {}) => {
  const errors = {};
  if (accion === ACCIONES.REGISTRAR_CONTACTO) {
    if (!form.canal) errors.canal = "Elegí el canal de contacto";
    if (!form.resultado) errors.resultado = "Elegí el resultado del contacto";
  }
  if (accion === ACCIONES.CAMBIAR_PRIORIDAD) {
    if (!form.prioridad) errors.prioridad = "Elegí la prioridad";
    else if (gestion?.prioridadManual && form.prioridad === codeOf(gestion?.prioridad)) {
      errors.prioridad = "Elegí una prioridad distinta de la actual";
    }
  }
  checkObservacion(errors, form.observacion, OBSERVACION_OBLIGATORIA[accion]);

  const proximo = text(form.proximoContacto);
  if (proximoContactoObligatorio(accion) && !proximo) {
    errors.proximoContacto = "Ingresá la fecha y hora del próximo contacto";
  } else if (proximo && (accion === ACCIONES.REGISTRAR_CONTACTO || proximoContactoObligatorio(accion))) {
    const fecha = parseInputDateTime(proximo);
    if (!fecha || fecha <= now) errors.proximoContacto = "Elegí una fecha posterior a ahora";
  }
  return errors;
};

// Body of POST gestiones/{id}/acciones without versionToken and correlationId (the modal adds them). Dates travel in
// UTC; the server converts them to its local time (RN7).
export const buildAccionPayload = (accion, form) => {
  const observacion = textOrNull(form.observacion);
  switch (accion) {
    case ACCIONES.REGISTRAR_CONTACTO:
      return {
        accion,
        canal: form.canal,
        resultadoContacto: form.resultado,
        fechaProximoContacto: toServerDateTime(form.proximoContacto),
        observacion,
      };
    case ACCIONES.PEDIR_DATOS:
    case ACCIONES.DERIVAR:
    case ACCIONES.REPROGRAMAR:
      return { accion, fechaProximoContacto: toServerDateTime(form.proximoContacto), observacion };
    case ACCIONES.CAMBIAR_PRIORIDAD:
      return { accion, prioridad: form.prioridad, observacion };
    default:
      return { accion, observacion };
  }
};

export const validateFinalizar = (form) => {
  const errors = {};
  if (!form.resultado) errors.resultado = "Elegí el resultado";
  else if (!form.motivoCierre) errors.motivoCierre = "Elegí el motivo de cierre";
  checkObservacion(errors, form.observacion, form.resultado === "DESCARTADA" ? "Contá por qué se descarta" : null);
  return errors;
};

export const buildFinalizarPayload = (form) => ({
  accion: ACCIONES.FINALIZAR,
  resultado: form.resultado,
  motivoCierre: form.motivoCierre,
  observacion: textOrNull(form.observacion),
});

export const CONFLICT_TITLE = "Esta gestión fue actualizada por otro usuario.";

// Only a stale gestión asks to reload: the version conflict (the server's concurrency message, IpcGestionService) or a
// gestión another user already finalized (checked before the version). Any other 409 ("La gestión ya está asignada a
// ese asesor.", ...) shows the server message.
const STALE_MESSAGES = [CONFLICT_TITLE, "La gestión ya está finalizada."];
export const isConflict = (error) => error?.status === 409 && STALE_MESSAGES.includes(String(error?.message || "").trim());

// Digits of a CUIT ("30-71111111-9" -> "30711111119"), "" without one: Altas/Bajas searches the CUIT by its digits.
export const cuitDigits = (value) => String(value ?? "").replace(/\D/g, "");

// A network failure or a server error may be retried with the same correlationId; a rejected request (4xx) shows the
// server message, which says what to change.
export const describeSaveError = (error) => {
  const status = Number(error?.status) || 0;
  const retry = status === 0 || status >= 500;
  return {
    title: "No pudimos guardar los cambios.",
    text: status && status < 500 ? error.message || "Probá de nuevo en unos segundos." : "Revisá la conexión y probá de nuevo. Lo que cargaste se mantiene.",
    retry,
  };
};

// "Reclamo por Liquidación", "Proactiva por Visita comercial".
export const tipoMotivo = (gestion) =>
  `${isProactiva(gestion) ? "Proactiva" : TIPO_LABELS[codeOf(gestion?.tipo)] || "Gestión"} por ${motivoLabel(gestion)}`;

// ---- Links of the gestión (Prompt 7, N8): buttons by motive only on an open Modificación that can be managed.
const MOTIVO_LINKS = {
  DATOS_BANCARIOS: ["editar"],
  DATOS_FISCALES: ["editar"],
  DOMICILIO: ["editar"],
  DATOS_TERMINAL: ["editar", "altas"],
  TITULARIDAD: ["altas"],
};

export const motivoLinks = (gestion, editable) => {
  if (!editable || !isOpenGestion(gestion) || !isOpenEstado(gestion?.estado)) return [];
  if (isProactiva(gestion) || codeOf(gestion?.tipo) !== "MODIFICACION") return [];
  return MOTIVO_LINKS[codeOf(gestion?.motivoCodigo)] || [];
};

// ---- Timeline (GET gestiones/{id}.timeline, newest first). "datos" keeps the C# property names, read without case.
const dateTime = (value) => {
  if (!value) return null;
  const time = formatTime(value);
  return time ? `${formatDate(value)} ${time}` : formatDate(value);
};

const proximoText = (datos) => {
  const fecha = dateTime(datoOf(datos, "FechaProximoContacto"));
  return fecha ? ` Próximo contacto: ${fecha}.` : "";
};

const nombre = (value) => text(value) || "otro asesor";

export const describeTimelineEntry = (entry, gestion) => {
  const datos = entry?.datos;
  switch (codeOf(entry?.accion)) {
    case "CREAR": {
      const canal = datoOf(datos, "Canal") || gestion?.canal;
      const subject = {
        categoria: datoOf(datos, "Categoria") || gestion?.categoria,
        tipo: datoOf(datos, "Tipo") || gestion?.tipo,
        motivoCodigo: datoOf(datos, "MotivoCodigo") || gestion?.motivoCodigo,
        motivoTexto: gestion?.motivoTexto,
      };
      return `Registró la gestión${canal ? ` por ${labelFor(CANAL_LABELS, canal)}` : ""}: ${tipoMotivo(subject)}.`;
    }
    case "REGISTRAR_CONTACTO": {
      const canal = labelFor(CANAL_LABELS, datoOf(datos, "Canal"));
      const resultado = labelFor(RESULTADO_CONTACTO_LABELS, datoOf(datos, "ResultadoContacto"));
      return `Registró un contacto por ${canal}: ${resultado}.${proximoText(datos)}`;
    }
    case "PEDIR_DATOS":
      return `Pidió datos al comercio.${proximoText(datos)}`;
    case "DERIVAR":
      return `Derivó la gestión.${proximoText(datos)}`;
    case "RETOMAR":
      if (codeOf(entry.estadoAnterior) === "ESPERANDO_COMERCIO") return "El comercio respondió.";
      if (codeOf(entry.estadoAnterior) === "DERIVADA") return "Volvió del área.";
      return "Retomó la gestión.";
    case "REPROGRAMAR": {
      const fecha = dateTime(datoOf(datos, "FechaProximoContacto"));
      return fecha ? `Reprogramó el próximo contacto para el ${fecha}.` : "Reprogramó el próximo contacto.";
    }
    case "OBSERVAR":
      return "Agregó una observación.";
    case "CAMBIAR_PRIORIDAD":
      return `Cambió la prioridad a ${labelFor(PRIORIDAD_LABELS, datoOf(datos, "PrioridadNueva"))}. Queda fijada a mano.`;
    case "FINALIZAR": {
      const motivo = datoOf(datos, "MotivoCierre");
      return `Finalizó la gestión: ${labelFor(ESTADO_LABELS, entry.estadoNuevo)}${motivo ? ` (${labelFor(MOTIVO_CIERRE_LABELS, motivo)})` : ""}.`;
    }
    case "TOMAR":
      return "Tomó la gestión.";
    case "ASIGNAR":
      return entry.responsableAnteriorNombre
        ? `Reasignó la gestión de ${entry.responsableAnteriorNombre} a ${nombre(entry.responsableNuevoNombre)}.`
        : `Asignó la gestión a ${nombre(entry.responsableNuevoNombre)}.`;
    case "LIBERAR":
      return entry.responsableAnteriorNombre && entry.responsableAnteriorNombre !== entry.usuarioNombre
        ? `Liberó la gestión de ${entry.responsableAnteriorNombre}. Quedó sin asignar.`
        : "Liberó la gestión. Quedó sin asignar.";
    default:
      return text(entry?.accion) || "Actualizó la gestión.";
  }
};

// "20/10/2026 · 16:40 · Martín Gómez".
export const timelineWhen = (entry) =>
  [formatDate(entry?.fecha), formatTime(entry?.fecha), text(entry?.usuarioNombre)].filter((part) => part && part !== EMPTY).join(" · ");

// The state change is only drawn when there is one; CREAR shows the state it was born with.
export const estadoChange = (entry) => {
  const from = codeOf(entry?.estadoAnterior);
  const to = codeOf(entry?.estadoNuevo);
  if (!to || from === to) return null;
  return { from: from || null, to };
};
